import type * as React from 'react';
import type { ScaleContinuousNumeric } from 'd3-scale';
import type { StockData } from './chart';
import type { Coordinate } from './properties';
import type {
  BaseStorageShape,
  TrendLineStorageShape,
  RayStorageShape,
  InfoLineStorageShape,
  HorzLineStorageShape,
  VertLineStorageShape,
  ElliottImpulseStorageShape,
  ElliottCorrectionABCStorageShape,
  FibRetracementStorageShape,
  FibChannelStorageShape,
  GannBoxStorageShape,
  GannFanStorageShape,
  TextStorageShape,
  ParallelChannelStorageShape,
  RectangleStorageShape,
  TriangleStorageShape,
  PathStorageShape,
  XABCDPatternStorageShape,
  FixedRangeVolumeProfileStorageShape,
  AnchoredVolumeProfileStorageShape,
  OrderLineStorageShape,
  StrokeDashTypes,
} from './shape';
export { VertexType } from './shape';

// ============================================================================
// Shape Props Types — common/lib/shape.ts의 StorageShape 타입에서 파생
// Props = StorageShape에서 메타데이터를 제외한 UI property popup 편집용 타입
// ============================================================================

/** Shape 저장용 메타데이터 필드 — Props에서 제외 */
type ShapeMetadata = 'uuid' | 'chartId' | 'order' | 'createdAt' | 'updatedAt';

// --- Lines: TrendLine, Ray, InfoLine (공유) ---
export type ExtensibleInfoLineProps =
  | Omit<TrendLineStorageShape, ShapeMetadata>
  | Omit<RayStorageShape, ShapeMetadata>
  | Omit<InfoLineStorageShape, ShapeMetadata>;

// --- HorzLine, VertLine (coordinates 미포함) ---
export type HorzLineProps = Omit<HorzLineStorageShape, ShapeMetadata | 'coordinates'>;
export type VertLineProps = Omit<VertLineStorageShape, ShapeMetadata | 'coordinates'>;

// --- Elliott Wave ---
export type ElliottImpulseProps = Omit<ElliottImpulseStorageShape, ShapeMetadata>;
export type ElliottCorrectionABCProps = Omit<ElliottCorrectionABCStorageShape, ShapeMetadata>;

// --- Fibonacci ---
export type FibRetracementProps = Omit<FibRetracementStorageShape, ShapeMetadata>;
export type FibChannelProps = Omit<FibChannelStorageShape, ShapeMetadata>;

// --- Gann ---
export type GannBoxProps = Omit<GannBoxStorageShape, ShapeMetadata>;
export type GannFanProps = Omit<GannFanStorageShape, ShapeMetadata>;

// --- Text ---
export type TextProps = Omit<TextStorageShape, ShapeMetadata>;

// --- Channels ---
export type ParallelChannelProps = Omit<ParallelChannelStorageShape, ShapeMetadata>;

// --- Geometric Shapes (coordinates 미포함) ---
export type RectangleProps = Omit<RectangleStorageShape, ShapeMetadata | 'coordinates'>;
export type TriangleProps = Omit<TriangleStorageShape, ShapeMetadata | 'coordinates'>;
export type PathProps = Omit<PathStorageShape, ShapeMetadata | 'coordinates'>;

// --- Patterns (coordinates 미포함) ---
export type XABCDPatternProps = Omit<XABCDPatternStorageShape, ShapeMetadata | 'coordinates'>;

// --- Order Line ---
export type OrderLineProps = Omit<OrderLineStorageShape, ShapeMetadata | 'coordinates'>;

// --- Volume Profile ---
export type FixedRangeVolumeProfileProps = Omit<FixedRangeVolumeProfileStorageShape, ShapeMetadata>;
export type AnchoredVolumeProfileProps = Omit<AnchoredVolumeProfileStorageShape, ShapeMetadata>;

/** Volume Profile type union */
export type VolumeProfileProps = FixedRangeVolumeProfileProps | AnchoredVolumeProfileProps;

export enum ShapePropertyInputType {
  ARROW_OPTION = 'ArrowOption',
  CHECKBOX = 'Checkbox',
  COLOR_PICKER = 'ColorPicker',
  EXTEND_OPTION = 'ExtendOption',
  FONT_OPTION = 'FontOption',
  FONT_BASIC_OPTION = 'FontBasicOption',
  LINE_OPTION = 'LineOption',
  LINE_WIDTH_DASH_OPTION = 'LineWidthDashOption',
  NUMBER = 'Number',
  SELECT = 'Select',
  SEPARATOR = 'Separator',
  SHOW_COLOR_OPTION = 'ShowColorOption',
  SHOW_COLOR_OPTIONS = 'ShowColorOptions',
  SHOW_OPACITY_OPTION = 'ShowOpacityOption',
  SHOW_VALUE_COLOR_OPTIONS = 'ShowValueColorOptions',
  SHOW_LINE_OPTION = 'ShowLineOption',
  TEXT = 'Text',
}
export interface ShapePropertyInputDef {
  name: string;
  type: ShapePropertyInputType;
  label: string;
  showInMiniToolbar?: boolean;
  min?: number;
  max?: number;
  step?: number;
  options?: string[];
  disabledAlpha?: boolean;
}

/** Shape types enum — common에서 re-export */
import { ShapeType } from './shape';
export { ShapeType };

export enum ToolbarButtonCategory {
  MEASURE = 'Measure',
  LINES = 'Lines',
  FIBONACCI = 'Fibonacci',
  GANN = 'Gann',
  PATTERNS = 'Patterns',
  GEOMETRIC_SHAPES = 'GeometricShapes',
  VOLUME = 'Volume',
  TEXT = 'Text',
  SHAPE_LIST_VIEW = 'ShapeListView',
}

/**
 * ShapePropsByType: ShapeType별 도형 속성 타입 매핑.
 *
 * shapes.ts의 Props 타입을 참조. 이들은 UI 속성 + coordinates를 포함하는 "full" Props이며,
 * common의 StorageShape variant(BaseStorageShape + props)와 구조적으로 일치한다.
 * MEASURE은 저장 비대상이므로 unknown[]로 유지.
 */
export interface ShapePropsByType {
  [ShapeType.MEASURE]: unknown[];
  [ShapeType.TREND_LINE]: ExtensibleInfoLineProps;
  [ShapeType.RAY]: ExtensibleInfoLineProps;
  [ShapeType.INFO_LINE]: ExtensibleInfoLineProps;
  [ShapeType.HORZ_LINE]: HorzLineProps;
  [ShapeType.VERT_LINE]: VertLineProps;
  [ShapeType.ELLIOTT_IMPULSE_12345]: ElliottImpulseProps;
  [ShapeType.FIB_RETRACEMENT]: FibRetracementProps;
  [ShapeType.FIB_CHANNEL]: FibChannelProps;
  [ShapeType.GANN_BOX]: GannBoxProps;
  [ShapeType.GANN_FAN]: GannFanProps;
  [ShapeType.TEXT]: TextProps;
  [ShapeType.ELLIOTT_CORRECTION_ABC]: ElliottCorrectionABCProps;
  [ShapeType.PARALLEL_CHANNEL]: ParallelChannelProps;
  [ShapeType.RECTANGLE]: RectangleProps;
  [ShapeType.TRIANGLE]: TriangleProps;
  [ShapeType.PATH]: PathProps;
  [ShapeType.FIXED_RANGE_VOLUME_PROFILE]: FixedRangeVolumeProfileProps;
  [ShapeType.XABCD_PATTERN]: XABCDPatternProps;
  [ShapeType.ORDER_LINE]: OrderLineProps;
  [ShapeType.ANCHORED_VOLUME_PROFILE]: AnchoredVolumeProfileProps;
}

/**
 * react-financial-charts 라이브러리 인터랙티브 도형의 내부 데이터 포맷.
 * TrendLine, FibChannel 등 각 도형 컴포넌트의 trends/channels 배열 항목 타입.
 * 라이브러리가 관리하는 포맷이므로 인덱스 시그니처로 확장 허용.
 */
export interface RFCInteractiveItem {
  id?: string | number;
  type?: string;
  selected?: boolean;
  hidden?: boolean;
  locked?: boolean;
  start?: [number, number];
  end?: [number, number];
  appearance?: Record<string, unknown>;
  barsRange?: number;
  priceRange?: number;
  daysBetween?: object;
  totalPercentChange?: number;
  angle?: number;
  [key: string]: unknown;
}

/**
 * RenderNode 헬퍼 타입.
 * StorageShape에서 `coordinates`(직렬화 포맷) 필드만 제외하고,
 * runtime 전용 필드(hidden/locked/node)를 덧붙인다.
 * 좌표는 각 variant에서 RFC가 사용하는 전용 필드(start/end, startXY, points 등)로 mixin한다.
 */
export type RenderNodeOf<S extends BaseStorageShape> = Omit<S, 'coordinates'> & {
  node?: unknown;
};

// ─── 도형별 RenderNode variants ─────────────────────────────────
// 각 variant는 `RenderNodeOf<XxxStorageShape> & 좌표mixin` 패턴으로 정의된다.
// type 디스크리미네이터·UI 속성은 BaseStorageShape + StorageShape에서 자동 상속된다.
// 좌표 mixin은 RFC 래퍼가 요구하는 runtime 포맷(start/end, startXY, points 등)을 추가한다.

/** TrendLine/Ray/InfoLine 공통 좌표: start/end (RFC의 EachTrendLine이 사용) */
interface StartEndCoords {
  start?: [number, number];
  end?: [number, number];
}

/** FibChannel/ParallelChannel/Triangle/GannBox/GannFan 공통 좌표: startXY/endXY/endXY2 */
interface ThreePointCoords {
  startXY?: [number, number];
  endXY?: [number, number];
  endXY2?: [number, number];
  dy?: number;
}

/** ElliottImpulse/ElliottCorrectionABC/XABCDPattern/Path 공통 좌표: points */
interface MultiPointCoords {
  points?: [number, number][];
}

// --- Lines: TrendLine, Ray, InfoLine (start/end 좌표 사용) ---
export type ExtensibleInfoLineRenderNode =
  | (RenderNodeOf<TrendLineStorageShape> & StartEndCoords)
  | (RenderNodeOf<RayStorageShape> & StartEndCoords)
  | (RenderNodeOf<InfoLineStorageShape> & StartEndCoords);

// --- HorzLine: yValue 좌표 사용 ---
export type HorzLineRenderNode = RenderNodeOf<HorzLineStorageShape> & {
  yValue?: number;
};

// --- VertLine: start/end/xValue 좌표 사용 ---
export type VertLineRenderNode = RenderNodeOf<VertLineStorageShape> &
  StartEndCoords & {
    xValue?: number;
    originChartId?: ChartId;
  };

// --- ParallelChannel: startXY/endXY/endXY2 좌표 사용 ---
export type ParallelChannelRenderNode = RenderNodeOf<ParallelChannelStorageShape> & ThreePointCoords;

// --- FibRetracement: x1/y1/x2/y2 좌표 사용 ---
export type FibRetracementRenderNode = RenderNodeOf<FibRetracementStorageShape> & {
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
};

// --- FibChannel: startXY/endXY/endXY2/dy 좌표 사용 ---
export type FibChannelRenderNode = RenderNodeOf<FibChannelStorageShape> & ThreePointCoords;

// --- GannBox: startXY/endXY 좌표 사용 ---
export type GannBoxRenderNode = RenderNodeOf<GannBoxStorageShape> & ThreePointCoords;

// --- GannFan: startXY/endXY 좌표 사용 ---
export type GannFanRenderNode = RenderNodeOf<GannFanStorageShape> & ThreePointCoords;

// --- ElliottImpulse: points 좌표 사용 ---
export type ElliottImpulseRenderNode = RenderNodeOf<ElliottImpulseStorageShape> & MultiPointCoords;

// --- ElliottCorrectionABC: points 좌표 사용 ---
export type ElliottCorrectionABCRenderNode = RenderNodeOf<ElliottCorrectionABCStorageShape> & MultiPointCoords;

// --- XABCDPattern: points 좌표 사용 ---
export type XABCDPatternRenderNode = RenderNodeOf<XABCDPatternStorageShape> & MultiPointCoords;

// --- Rectangle: start/end 좌표 사용 ---
export type RectangleRenderNode = RenderNodeOf<RectangleStorageShape> & StartEndCoords;

// --- Triangle: startXY/endXY/endXY2 좌표 사용 ---
export type TriangleRenderNode = RenderNodeOf<TriangleStorageShape> & ThreePointCoords;

// --- Path: points 좌표 사용 ---
export type PathRenderNode = RenderNodeOf<PathStorageShape> & MultiPointCoords;

// --- Text: position 좌표 사용 ---
export type TextRenderNode = RenderNodeOf<TextStorageShape> & {
  position?: [number, number];
};

// --- Volume Profile ---
export type FixedRangeVolumeProfileRenderNode = RenderNodeOf<FixedRangeVolumeProfileStorageShape> & StartEndCoords;

export type AnchoredVolumeProfileRenderNode = RenderNodeOf<AnchoredVolumeProfileStorageShape> & {
  start?: [number, number];
};

// ============================================================================
// 드래그 중 임시 상태(override)의 공통 베이스 타입
// ============================================================================

/**
 * 드래그 중 한 도형의 임시 좌표·속성을 보관하는 override의 공통 베이스.
 *
 * 모든 도형 컴포넌트의 InteractiveShape state.override는 이 인터페이스를 확장한다.
 * `uuid`로 "어느 도형이 드래그 중인가"를 식별하고, 카테고리별·도형별로 추가 필드를 더한다.
 *
 * 라이프사이클:
 *   onDragStart → setState({ override: { uuid } })
 *   onDrag      → setState({ override: { uuid, ...newCoords } })
 *   render      → each.uuid === override.uuid 인 도형만 override 좌표로 그림
 *   onDragComplete → 상위에 전파 후 setState({ override: undefined })
 */
export interface DragOverrideBase {
  uuid: string;
}

/**
 * 2점 좌표 드래그 도형용 override.
 * TrendLine, Ray, InfoLine, HorzLine, VertLine, Rectangle, FibonacciRetracement 등에서 사용.
 */
export interface CoordDragOverride extends DragOverrideBase {
  x1Value?: number;
  y1Value?: number;
  x2Value?: number;
  y2Value?: number;
}

/**
 * Volume Profile 계열(FRVP, AVP) override.
 * 4개 좌표 + AVP의 anchorX 옵션.
 */
export interface VolumeProfileDragOverride extends DragOverrideBase {
  x1Value?: number;
  x2Value?: number;
  y1Value?: number;
  y2Value?: number;
  anchorX?: number;
}

/**
 * Multi-point 도형(Elliott, Path) override.
 * - 드래그 시작: `{ uuid }`만 있을 수 있음 (좌표 미정)
 * - 전체 점 갱신: `{ uuid, newPoints }`
 * - 단일 점 갱신: `{ uuid, pointIdx, newPoint }`
 *
 * 컴포넌트는 `'newPoints' in override` 등으로 분기 처리한다.
 */
export interface MultiPointDragOverride extends DragOverrideBase {
  newPoints?: [number, number][];
  pointIdx?: number;
  newPoint?: [number, number];
}

/**
 * react-financial-charts의 `EdgeCoordinate` 렌더 옵션.
 * `PriceLine`이 직접 받아 RFC 내부로 흘려보낸다.
 * `ORDER_LINE_RENDER_DEFAULTS.edge`도 이 형태를 따른다.
 */
export interface EdgeConfig {
  readonly displayFormat?: (value: number) => string;
  readonly arrowWidth?: number;
  readonly at?: 'left' | 'right';
  readonly dx?: number;
  readonly fill?: string;
  readonly fillOpacity?: number;
  readonly fitToText?: boolean;
  readonly opacity?: number;
  readonly orient?: 'left' | 'right';
  readonly r?: number;
  readonly rectWidth?: number;
  readonly rectHeight?: number;
  readonly stroke?: string;
  readonly strokeOpacity?: number;
  readonly strokeWidth?: number;
}

/**
 * OrderLine 렌더링용 스타일 필드.
 * `shapeConverter.storageToRenderNode`가 `ORDER_LINE_RENDER_DEFAULTS`를 spread하여 항상 주입한다.
 * `EachInteractiveOrderLineProps`가 직접 사용하는 필드들과 1:1 매칭한다.
 */
export interface OrderLineRenderStyle {
  bgFill: string;
  bgOpacity: number;
  stroke: string;
  strokeOpacity: number;
  strokeDasharray: StrokeDashTypes;
  strokeWidth: number;
  textFill: string;
  fontFamily: string;
  fontSize: number;
  fontStyle: string;
  fontWeight: string;
  text: string;
  draggable: boolean;
  selected: boolean;
  textBox: {
    coordinateValue: { height: number; right: number; padding: { left: number; right: number } };
    selectedType: { height: number; right: number; padding: { left: number; right: number } };
    closeIcon: { height: number; right: number; padding: { left: number; right: number } };
  };
  edge: EdgeConfig;
}

/**
 * OrderLineRenderNode — 특수 케이스.
 * storage의 `price`(체결 기준가)와 `positionType`(storage 포맷)은 runtime에서 의미가 다르므로 제거하고,
 * runtime 전용 `yValue`(드래그 중 y 좌표)와 로컬 `positionType: PositionType`을 추가한다.
 * `tpsl`, `amount`, `uuid`, `chartId`, `type: ShapeType.ORDER_LINE` 등은 storage에서 자동 상속.
 *
 * 스타일 필드(`OrderLineRenderStyle`)는 `ORDER_LINE_RENDER_DEFAULTS`가 storageToRenderNode에서
 * spread되어 런타임에 항상 채워진다. 따라서 `EachInteractiveOrderLineProps`에 직접 spread 가능.
 */
export type OrderLineRenderNode = Omit<OrderLineStorageShape, 'coordinates' | 'price' | 'positionType'> &
  OrderLineRenderStyle & {
    yValue: number;
    /** drag complete 시 storage 저장용 좌표. runtime 전용. */
    coordinates?: Coordinate[];
    positionType: PositionType;
    /** 거래소에서 실제 포지션이 진입된 상태 */
    isEntered?: boolean;
    hidden?: boolean;
    locked?: boolean;
    node?: unknown;
    /**
     * TP/SL 절대 가격 — runtime 전용. storage의 tpsl(netROE)를 load 시 변환한 값.
     * 드래그·렌더링 등 모든 내부 계산에 사용. storage에는 저장하지 않는다.
     */
    tpslPrice: Array<{ tp: number; sl: number }>;
    // tpsl는 OrderLineStorageShape에서 상속 — storage와 render node 양쪽에 동일하게 존재
  };

/**
 * MeasureRenderNode — 특수 케이스 (storage 없음, transient only).
 * RenderNodeOf 헬퍼 미사용. 직접 정의한다.
 */
export interface MeasureRenderNode {
  /** 도형 종류 판별자 (discriminated union의 판별 필드). */
  type: ShapeType.MEASURE;
  uuid: string;
  chartId: ChartId;
  createdAt: number;
  updatedAt: number;
  order?: number;
  hidden?: boolean;
  locked?: boolean;
  node?: unknown;
  coordinates?: Coordinate[];
  start?: [number, number];
  end?: [number, number];
}

/**
 * RenderNode — Discriminated Union.
 * type 필드(ShapeType)로 TypeScript가 자동으로 variant를 narrowing한다.
 * RFCInteractiveItem 상속을 끊고, [key: string]: unknown 인덱스 시그니처를 제거하여
 * 정확한 타입 안전성을 확보.
 *
 * 주의: `type` 필드는 RFC 렌더링 모드 ('LINE', 'BOUND' 등)이며, ShapeType이 아님.
 *       도형 종류 판별은 반드시 `type` 필드를 사용.
 */
export type RenderNode =
  | ExtensibleInfoLineRenderNode
  | HorzLineRenderNode
  | VertLineRenderNode
  | ParallelChannelRenderNode
  | FibRetracementRenderNode
  | FibChannelRenderNode
  | GannBoxRenderNode
  | GannFanRenderNode
  | ElliottImpulseRenderNode
  | ElliottCorrectionABCRenderNode
  | XABCDPatternRenderNode
  | RectangleRenderNode
  | TriangleRenderNode
  | PathRenderNode
  | TextRenderNode
  | FixedRangeVolumeProfileRenderNode
  | AnchoredVolumeProfileRenderNode
  | OrderLineRenderNode
  | MeasureRenderNode;

/**
 * RenderNode → RFCInteractiveItem 변환 헬퍼.
 * RFC 경계(ShapeRender, ShapeManager)에서 라이브러리에 데이터를 전달할 때 사용.
 * 스프레드로 모든 필드를 복사하고, RFC가 요구하는 인덱스 시그니처 호환 객체를 반환.
 *
 * @example
 * const rfcItem = toRFCItem(node);
 * <EachTrendLine trends={nodes.map(toRFCItem)} />
 */
export function toRFCItem(node: RenderNode): RFCInteractiveItem {
  // 알 수 없는 ShapeType 런타임 가드 (exhaustive check)
  // RFC 라이브러리가 도형 그리기 중 shapeType이 없는 임시 노드를 생성할 수 있음
  // 이 경우 검증 없이 스프레드로 통과
  const _shapeType = node.type;
  if (_shapeType === undefined) {
    return node;
  }
  switch (_shapeType) {
    case ShapeType.TREND_LINE:
    case ShapeType.RAY:
    case ShapeType.INFO_LINE:
    case ShapeType.HORZ_LINE:
    case ShapeType.VERT_LINE:
    case ShapeType.PARALLEL_CHANNEL:
    case ShapeType.FIB_RETRACEMENT:
    case ShapeType.FIB_CHANNEL:
    case ShapeType.GANN_BOX:
    case ShapeType.GANN_FAN:
    case ShapeType.ELLIOTT_IMPULSE_12345:
    case ShapeType.ELLIOTT_CORRECTION_ABC:
    case ShapeType.XABCD_PATTERN:
    case ShapeType.RECTANGLE:
    case ShapeType.TRIANGLE:
    case ShapeType.PATH:
    case ShapeType.TEXT:
    case ShapeType.FIXED_RANGE_VOLUME_PROFILE:
    case ShapeType.ANCHORED_VOLUME_PROFILE:
    case ShapeType.ORDER_LINE:
    case ShapeType.MEASURE:
      break;
    default: {
      // exhaustive check — 새 ShapeType이 추가되면 여기서 컴파일 에러 발생
      const _exhaustive: never = _shapeType;
      throw new Error(`Unknown ShapeType: ${_exhaustive}`);
    }
  }

  // RFCInteractiveItem은 [key: string]: unknown 인덱스 시그니처를 가지므로
  // 스프레드로 모든 필드를 복사하면 호환됨

  return { ...node };
}

/**
 * react-financial-charts의 canvasDraw, isHover 등 콜백에 전달되는 context 객체.
 * 라이브러리가 추가 필드를 주입할 수 있으므로 인덱스 시그니처를 포함한다.
 */
export interface MorePropsChartConfig {
  height: number;
  width: number;
  origin: [number, number];
  yScale: ScaleContinuousNumeric<number, number>;
  flipYScale?: boolean;
  padding?: number | { top?: number; bottom?: number; left?: number; right?: number };
  [key: string]: unknown;
}

export interface MoreProps<TItem = StockData> {
  hovering?: boolean;
  mouseXY: [number, number];
  /**
   * MoreProps는 hover, select, drag 등 모든 콜백에 공통으로 전달되는 타입인데,
   * startPos는 그 중 drag 콜백에서만 값이 있고 나머지에서는
   * undefined입니다. 라이브러리의 공식 선언 역시 startPos?: [number, number]로 optional입니다.
   * startPos를 드래그 관련 이벤트 핸들러 안에서 "!"를 붙여서 쓰는 이유가 바로 이 맥락 때문입니다.
   * 해당 코드들은 모두 handleDrag 류의 핸들러 안에 있고, 개발자가 "여기서는 startPos가 반드시 있다"는
   * 것을 알고 단언하는 것이므로 !는 적절한 사용입니다.
   * 아래 선언에서 "?"를 제거하면 hover 콜백 등에서 startPos가 항상 있는 것처럼 타입이 잘못 표현됩니다.
   */
  startPos?: [number, number];
  xScale: ScaleContinuousNumeric<number, number>;
  xAccessor: (item: TItem) => number;
  displayXAccessor: (item: TItem) => Date;
  currentItem: TItem;
  fullData: TItem[];
  plotData: TItem[];
  chartConfig: MorePropsChartConfig;
  show?: boolean;
  height?: number;
  width?: number;
  ratio?: number;
  currentCharts?: string[];
  chartConfigs?: unknown[];
  chartId?: ChartId;
  [key: string]: unknown;
}

export interface DynamicComponentProps<T> {
  label?: string;
  value: T;
  onChange: (newValue: T) => void;
  disabled?: boolean;
  type?: string;
  isFromMiniToolbar?: boolean;
  svgIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

/**
 * Valid chart ID type in string format with 'chartId_' prefix.
 * This prevents accidentally accessing with ShapeType (string enum without prefix) as the first key.
 * Examples: 'chartId_volume', 'chartId_ohlcv', 'chartId_full_sto', 'chartId_rsi', 'chartId_elder_ray', 'chartId_volume_2'
 */
export type ChartId = `chartId_${string}`;

/**
 * ShapeType → 도형별 RenderNode variant 매핑.
 * RenderNodesMap에서 특정 ShapeType 키로 접근하면 해당 도형의 RenderNode 타입이 반환된다.
 */
export interface RenderNodeMap {
  [ShapeType.TREND_LINE]: ExtensibleInfoLineRenderNode;
  [ShapeType.RAY]: ExtensibleInfoLineRenderNode;
  [ShapeType.INFO_LINE]: ExtensibleInfoLineRenderNode;
  [ShapeType.HORZ_LINE]: HorzLineRenderNode;
  [ShapeType.VERT_LINE]: VertLineRenderNode;
  [ShapeType.PARALLEL_CHANNEL]: ParallelChannelRenderNode;
  [ShapeType.FIB_RETRACEMENT]: FibRetracementRenderNode;
  [ShapeType.FIB_CHANNEL]: FibChannelRenderNode;
  [ShapeType.GANN_BOX]: GannBoxRenderNode;
  [ShapeType.GANN_FAN]: GannFanRenderNode;
  [ShapeType.ELLIOTT_IMPULSE_12345]: ElliottImpulseRenderNode;
  [ShapeType.ELLIOTT_CORRECTION_ABC]: ElliottCorrectionABCRenderNode;
  [ShapeType.XABCD_PATTERN]: XABCDPatternRenderNode;
  [ShapeType.RECTANGLE]: RectangleRenderNode;
  [ShapeType.TRIANGLE]: TriangleRenderNode;
  [ShapeType.PATH]: PathRenderNode;
  [ShapeType.TEXT]: TextRenderNode;
  [ShapeType.FIXED_RANGE_VOLUME_PROFILE]: FixedRangeVolumeProfileRenderNode;
  [ShapeType.ANCHORED_VOLUME_PROFILE]: AnchoredVolumeProfileRenderNode;
  [ShapeType.ORDER_LINE]: OrderLineRenderNode;
  [ShapeType.MEASURE]: MeasureRenderNode;
}

/**
 * Shapes data structure organized by chartId first, then by ShapeType.
 * Using ChartId template literal type prevents accidentally accessing with ShapeType as the first key.
 * 각 ShapeType 키로 접근하면 해당 도형의 RenderNode variant 배열이 반환된다.
 *
 * @example
 * renderNodes[chartId][ShapeType.TREND_LINE] // TrendLineRenderNode[] | undefined
 * renderNodes[chartId][ShapeType.FIB_RETRACEMENT] // FibRetracementRenderNode[] | undefined
 */
export type RenderNodesMap = Record<
  ChartId,
  {
    [K in ShapeType]?: RenderNodeMap[K][];
  }
>;

export interface ShapeSelection {
  type: ShapeType;
  mode?: 'edit' | 'create';
  uuid: string;
  chartId: ChartId;
  timeframe: string;
}

export interface Levels {
  show: boolean;
  value: number;
  color: string;
}

/**
 * HoverTextNearMouse 컴포넌트에 전달되는 hover 텍스트 설정.
 * Each* 래퍼 컴포넌트의 hoverText prop에 사용 (완성된 객체, 모든 필드 required).
 * 외부(public-facing) 컴포넌트에서는 Partial<HoverTextConfig>로 사용.
 */
export interface HoverTextConfig {
  enable: boolean;
  fontFamily: string;
  fontSize: number;
  fill: string;
  text: string;
  bgFill: string;
  bgOpacity: number;
  bgWidth: number | string;
  bgHeight: number | string;
  selectedText?: string;
}

/** 도형 그리기 상태 값 */
export type DrawingStatus = 'started' | 'completed' | 'idle';

/** 도형 그리기 상태의 공통 기반 필드 */
interface DrawingStateBase {
  status?: DrawingStatus;
  chartId?: ChartId;
  endXY?: [number, number]; // MultiPointDrawingState에서 required로 override
  endXY2?: [number, number];
  dy?: number;
  text?: TrendLineTextState; // TrendLine 전용
  id?: string;
}

/**
 * 그리기 비활성 상태 (도형 미선택).
 * 표준 도형 필드를 optional로 포함하여, 각 shape 컴포넌트가
 * type 체크 없이도 optional chaining으로 필드에 접근할 수 있게 한다.
 */
export interface IdleDrawingState extends DrawingStateBase {
  type?: undefined;
  startXY?: [number, number];
}

/**
 * 표준 2점 도형의 그리기 상태.
 * TrendLine, Ray, FibChannel, Rectangle 등 대부분의 도형에서 사용.
 */
export interface StandardDrawingState extends DrawingStateBase {
  type: Exclude<
    ShapeType,
    | ShapeType.MEASURE
    | ShapeType.ELLIOTT_IMPULSE_12345
    | ShapeType.ELLIOTT_CORRECTION_ABC
    | ShapeType.XABCD_PATTERN
    | ShapeType.PATH
  >;
  startXY?: [number, number];
}

/**
 * 다중 꼭짓점 도형의 그리기 상태.
 * ElliottImpulse / ElliottCorrection / XABCDPattern / Path 에서 사용.
 * currentPoints가 점 배열([number, number][])이며 status·endXY·chartId가 필수.
 */
export interface MultiPointDrawingState extends DrawingStateBase {
  type: ShapeType.ELLIOTT_IMPULSE_12345 | ShapeType.ELLIOTT_CORRECTION_ABC | ShapeType.XABCD_PATTERN | ShapeType.PATH;
  status: DrawingStatus;
  chartId: ChartId;
  currentPoints: [number, number][];
  startXY?: undefined; // MultiPoint에서는 항상 undefined — Union 전체 startXY 타입을 [number,number]|undefined로 유지
  endXY: [number, number]; // base의 optional을 required로 override
}

/**
 * Measure(측정) 도형의 그리기 상태.
 * 시작/끝 좌표를 { xValue, yValue } 형식으로 관리하는 점이 표준 도형과 다름.
 */
export interface MeasureDrawingState extends DrawingStateBase {
  type: ShapeType.MEASURE;
  startXY?: undefined; // Measure에서는 항상 undefined — Union 전체 startXY 타입을 [number,number]|undefined로 유지
  startPosition?: { xValue: number; yValue: number };
  end?: { xValue: number; yValue: number } | null;
  x1y1?: number[];
}

/**
 * 도형 그리기 상태 — Discriminated Union.
 * type 값으로 TypeScript가 자동으로 멤버 타입을 narrowing한다.
 */
export type DrawingSharedState = IdleDrawingState | StandardDrawingState | MultiPointDrawingState | MeasureDrawingState;

// 하위 호환 별칭
export type ElliottImpulseSharedState = MultiPointDrawingState;
export type MeasureSharedState = MeasureDrawingState;
export type TrendLineSharedState = StandardDrawingState;

/**
 * TrendLine 도형의 실시간 측정값 (StandardDrawingState.text에 저장).
 */
export interface TrendLineTextState {
  priceRange?: number;
  totalPercentChange?: number;
  barsRange?: number;
  daysBetween?: { days: number; hours: number; minutes: number };
  angle?: number;
}

export type ShapeAppearanceUpdater = (
  values: unknown,
  shape: { appearance?: Record<string, unknown>; [key: string]: unknown },
) => unknown;

export type ShapeConfigMap = {
  [key in ShapeType]?: ShapeAppearanceUpdater;
};

export interface ChartInfo {
  chartId: ChartId;
  height: number;
  label: string;
  isCandleChart?: boolean;
}

export interface RatioDropdown {
  uuid: string;
  hovering: boolean;
  position: {
    x: number;
    y: number;
  };
  positionType: EnterMode;
  color: string;
}

/** Position 방향 (long/short).
 *  @see common/lib/enum.ts의 `PositionType` enum과 값이 동일하다.
 *  chart-tools가 standalone 빌드를 지원해야 하므로 별도 정의 유지. */
export type PositionType = 'long' | 'short';
/** 주문 진입 방식 — UI에서 사용자가 선택하는 롱/숏/헷지 */
export type EnterMode = PositionType | 'hedge';

export type ActivePopup =
  | null
  | {
      type: 'y-popup';
      coords: { x: number; y: number };
      chartId: ChartId;
    }
  | {
      type: 'order-popup';
      coords: { x: number; y: number };
      position: EnterMode;
      chartId: ChartId;
    };

// Indicator types enum
export enum IndicatorType {
  EMA_12 = 'EMA_12',
  EMA_26 = 'EMA_26',
  RSI = 'RSI',
  FULL_STO = 'FULL_STO',
  ELDER_RAY = 'ELDER_RAY',
  VOLUME1 = 'VOLUME1',
  VOLUME = 'VOLUME',
}

export interface IndicatorObject {
  id: string;
  type: IndicatorType;
  label: string;
  options?: Record<string, unknown>;
}

export type IndicatorsDataStructure = Record<ChartId, IndicatorObject[]>;

/**
 * 도형 컴포넌트 간 broadcast용 override.
 *
 * `CoordDragOverride`(컴포넌트 내부 state.override)와 같은 좌표 필드를 공유하지만,
 * VertLine처럼 `chartId_common`에 그려져 여러 chart panel에 걸쳐 있는 도형이
 * React state 대신 `chartUIStore`의 transient channel로 broadcast할 때 사용된다.
 *
 * - `Partial<CoordDragOverride>`: 좌표·uuid 필드를 베이스에서 상속. 빈 상태(`{}`) 허용을 위해 모두 optional.
 * - `chartId`, `hover`: VertLine broadcast 전용 (어느 차트에서 어느 도형이 hover 중인지).
 *
 * 일반 도형의 컴포넌트 내부 드래그 버퍼는 `CoordDragOverride` 등을 직접 사용한다.
 */
export interface TransientBroadcastDragOverride extends Partial<CoordDragOverride> {
  chartId?: ChartId;
  hover?: string;
}
